import { useMemo, type FC } from 'react';
import { View } from 'react-native';
import { FAB } from 'react-native-paper';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { ArrowBackIcon, MAP_RESOURCE } from '@/assets';
import { APP_VERSION } from '@/configs/version';
import { useFeature } from '@/features/feature-management/provider/useFeature';
import type { EntertainmentFeature } from '@/features/feature-management/types/entertainmentFeature';
import { FeaturesEnum } from '@/features/feature-management/types/features';
import type {
  AppNativeStackScreenProps,
  AppRoute
} from '@/navigation/types/navigation';
import { EmbeddedWebpage } from '@/shared/components/embedded/EmbeddedWebpage';
import { useAppTheme } from '@/styles/hooks/useAppTheme';

type Props = AppNativeStackScreenProps<typeof AppRoute.OrganizationWebview>;

export const OrganizationWebviewScreen: FC<Props> = ({ navigation }) => {
  const theme = useAppTheme();

  const { getFeature } = useFeature();

  const { enabled, featureValues } = useMemo<EntertainmentFeature>(
    () => getFeature(FeaturesEnum.Entertainment),
    [getFeature]
  );

  const url = useMemo(
    () => (enabled ? featureValues.fullPageWebviewUrl : undefined),
    [enabled, featureValues.fullPageWebviewUrl]
  );

  const insets = useSafeAreaInsets();

  return (
    <View style={{ flex: 1 }}>
      <EmbeddedWebpage
        srcId={MAP_RESOURCE}
        src={url}
        onError={(e) => console.error(e.target)}
        openLinksExtern
        userAgentPostfix={`StadtLandFunk/${APP_VERSION}`}
        injectedJavascript={`document.documentElement.style.setProperty('--safe-area-inset-top', '${insets.top}px'); 
        document.documentElement.style.setProperty('--safe-area-inset-right', '${insets.right}px'); 
        document.documentElement.style.setProperty('--safe-area-inset-bottom', '${insets.bottom}px'); 
        document.documentElement.style.setProperty('--safe-area-inset-left', '${insets.left}px');`}
      />
      <FAB
        animated={false}
        icon={({ size }) => (
          <ArrowBackIcon
            fill={theme.colors.onPrimary}
            height={size}
            width={size}
          />
        )}
        style={{
          position: 'absolute',
          top: insets.top + 8,
          left: insets.left + 8,
          backgroundColor: theme.colors.primary,
          borderRadius: 30
        }}
        color={theme.colors.onPrimary}
        onPress={() => navigation.pop()}
      />
    </View>
  );
};
